import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ControlValueAccessor,
  Validator,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'ppl-dropdown-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DropdownModule],
  templateUrl: './dropdown-select.component.html',
  styleUrl: './dropdown-select.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DropdownSelectComponent),
      multi: true,
    },
  ],
})
export class DropdownSelectComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() options: any[] = [];
  @Input() optionLabel: any;
  @Input() optionValue: any;
  @Input() placeholder = 'Select';
  @Input() disabled = false; // this should be used for only template driven forms.
  @Input() showArrow = true;
  @Input() showClearButton = true;
  @Input() setPanelHeight = false;
  // When true, renders the option panel via CDK overlay appended to <body> instead of
  // inline after the trigger - lets it escape a parent section's height/overflow
  // constraints (which otherwise clip the panel down to just 2-3 visible options).
  @Input() appendToBody = false;
  @Input() ariaLabelledBy = '';
  @Input() arialabel: any;
  @Input() isRequired = false;
  @Output() selectionChange = new EventEmitter<any>();
  @ViewChild('dropdownContainer') dropdownContainerRef!: ElementRef;
  
  isControlDisabled = false;
  panelWidth = 0;
  pendingValue: any = null;
  selectedItem: any = null;
  ariaMessage = '';
  isFocused = false;
  setPanelWidth(): void {
    const inputWidth = this.dropdownContainerRef?.nativeElement?.offsetWidth;
    if (inputWidth) {
      this.panelWidth = inputWidth;
    }
    if (!this.options || this.options.length === 0) {
      this.ariaMessage = 'No results found';
    }
    if (this.allOptionsDisabled) {
        this.ariaMessage = `No selectable options available`;
      return;
    }
  }

  onChange = (_: any) => { };
  onTouched = () => { };

  get allOptionsDisabled(): boolean {
    return this.options?.length > 0 && this.options.every(opt => opt.disabled);
  }
  
  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['options'] &&
      this.options?.length > 0 &&
      this.pendingValue !== null
    ) {
      this.selectedItem = this.options.find(
        (o) => o[this.optionValue] === this.pendingValue
      );
      this.pendingValue = null;
    }
  }

  ngAfterViewInit(): void {
    // If we want to manually set the aria label for p dropdown component to readout the label
    // instead of the selected value, we can pass the id of the label element to ariaLabelledBy input.
    // primeng is overriding aria-label with placehoder value, so we need to set it after a timeout.
      setTimeout(() => {
        const input = this.dropdownContainerRef.nativeElement.querySelector('span[aria-label]');
        if (input) {
          if (this.ariaLabelledBy && this.ariaLabelledBy.length > 0) {
            input.setAttribute('aria-labelledby', this.ariaLabelledBy);
          } else if (this.arialabel && this.arialabel.length > 0) {
            input.setAttribute('aria-label', this.arialabel);
          }
        }
        // this.setPanelWidth();
    }, 200);
  }

  writeValue(obj: any): void {
    if (!obj) {
      this.selectedItem = null;
      return;
    }

    if (!this.options || this.options.length === 0) {
      this.pendingValue = obj;
      return;
    }

    this.selectedItem = this.findSelectedItem(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isControlDisabled = isDisabled || this.disabled;
  }

  validate() {
    return null;
  }

  onSelectionChange(value: any) {
    let code;
    if (typeof value === 'object') {
      if (this.optionValue) {
        // property mode
        code = value ? value[this.optionValue] : null;
      } else {
        // object mode (default NG-Zorro behavior)
        code = value;
      }
    } else {
      code = value;
    }
    this.selectedItem = value;
    this.onChange(code);
    this.selectionChange.emit(code);
    this.onFocus();
  }

  onFocus() {
    this.isFocused = true;
    this.announceSelectedValue();
    // setTimeout(() => this.announceSelectedValue(), 500);
  }

  onBlur() {
    this.isFocused = false;
    this.ariaMessage = '';
  }

  announceSelectedValue() {
    const label = this.arialabel || this.placeholder || 'Selection';
    if (this.selectedItem) {
      const selectedText = this.selectedItem[this.optionLabel] ?? this.selectedItem;
      this.ariaMessage = `${label}: ${selectedText} selected.`;
    } else {
      this.ariaMessage = `${label}. No item selected.`;
    }
  }

  private findSelectedItem(value: any): any {
    if (typeof this.options[0] === 'object') {
      if (this.optionValue) {
        // property mode
        return this.options.find((o) => o[this.optionValue!] === value) || null;
      } else {
        // object mode (default NG-Zorro behavior)
        return this.options.find(
          (o) => JSON.stringify(o) === JSON.stringify(value)
        ) || null;
      }
    } else {
      return value;
    }
  }
}
